/** CrmBlockRuns — the ONE renderer for a grouped Progress / New-Info block value.
 *
 * `crmBlockGroups.groupBlockItems` is the one *grouping* rule; this is the one *presentation* rule,
 * and the second existed for the same reason as the first. Three surfaces paint these runs — the
 * Fields grid (`CrmRecordPanel.BlockValue`), the Recent-Updates cluster (`CrmRecentUpdates`) and the
 * record timeline (`CrmRecordTimeline`, both its pinned queue and its settled stream) — and until this
 * component they each carried their own copy of the sub-header DOM plus the collapse branch.
 *
 * ⚠ THE COLLAPSE BRANCH IS SEMANTIC, NOT COSMETIC, which is why sharing it matters. An all-ungrouped
 * block — every payload written before the S2 prompt went live on 2026-08-10 — must render the BARE
 * `<ul>` these surfaces always rendered, with no wrapper: that is the pre-S2 compatibility guarantee,
 * pinned by the class-stripped goldens in `crmBlockGroups.test.tsx`. `CrmRecentUpdates` had no such
 * branch (it wrapped unconditionally), so the invariant held on two surfaces out of three purely by
 * coincidence of which copy someone edited last.
 *
 * The bullet LIST class is a prop, deliberately. The three surfaces have always sized and coloured
 * their bullets differently (`.blockBullets` at 13px/16px-indent, the timeline's `.bullets`, the
 * cluster's 18px indent); folding those together would be a visual change to two shipped surfaces
 * dressed up as a de-duplication. Only the group wrapper + sub-header — the part that was genuinely
 * duplicated by this work — lives in `CrmBlockRuns.module.css`.
 *
 * Renders NOTHING for an empty run list. The empty state belongs to the caller: the Fields grid shows
 * an em dash, the Recent-Updates cluster its own `emptyBlock` marker, and the timeline's pinned queue
 * an explicit "no content" line beside its Dismiss button (a contentless proposal must stay
 * dismissible — see `recordTimelineMerge.pendingItems`).
 */
import type { BlockGroup } from './crmBlockGroups';
import styles from './CrmBlockRuns.module.css';

interface Props {
  groups: BlockGroup[];
  /** 'new' = an active proposal (each surface's green), 'plain' = the settled / current narrative. */
  tone: 'new' | 'plain';
  /** The calling surface's own bullet-list class — see the note above. */
  listClassName: string;
  /** Edit mode: the `item_id`s the operator has marked for removal (rendered struck through). */
  droppedItemIds?: ReadonlySet<string>;
  /** Edit mode: PRESENT ⇒ every addressable bullet gets a drop / restore toggle.
   *
   * ⛔ Absent ⇒ the `<li>` is `{text}` and nothing else, which is what keeps the pre-S2 goldens and
   * the two read-only surfaces byte-identical. Edit affordances are ADDITIVE on an opt-in prop for
   * exactly that reason — a permanently-rendered control that happens to be hidden by CSS would put
   * the compatibility guarantee at the mercy of a stylesheet. */
  onToggleItem?: (itemId: string) => void;
}

export function CrmBlockRuns({
  groups, tone, listClassName, droppedItemIds, onToggleItem,
}: Props) {
  if (groups.length === 0) return null;
  const list = (items: BlockGroup['items']) => (
    <ul className={listClassName} data-block-tone={tone}>
      {items.map(({ itemId, text }, i) => {
        if (!onToggleItem) return <li key={i}>{text}</li>;
        const dropped = itemId != null && droppedItemIds?.has(itemId) === true;
        return (
          <li key={itemId ?? i} className={dropped ? styles.droppedItem : undefined}
              data-item-id={itemId ?? undefined} data-dropped={dropped ? 'y' : undefined}>
            <button
              type="button"
              className={styles.itemToggle}
              // An unaddressable bullet (no `item_id` — a legacy bare-string payload) cannot be named
              // in the edit-accept value, so the control is DISABLED rather than wired to a guess.
              disabled={itemId == null}
              title={itemId == null
                ? 'This bullet has no stable id (older payload) — it can’t be removed individually'
                : dropped ? 'Keep this bullet' : 'Remove this bullet from what gets accepted'}
              aria-label={dropped ? `Keep: ${text}` : `Remove: ${text}`}
              onClick={() => itemId != null && onToggleItem(itemId)}
            >
              {dropped ? '↺' : '×'}
            </button>
            <span>{text}</span>
          </li>
        );
      })}
    </ul>
  );
  // The pre-S2 compatibility path: exactly one headerless run ⇒ the bare list, unwrapped.
  if (groups.length === 1 && groups[0].group === null) return list(groups[0].items);
  return (
    <div className={styles.groups}>
      {groups.map(({ group, items }, gi) => (
        <div key={group ?? `__ungrouped-${gi}`} className={styles.group}>
          {group && <div className={styles.label} data-block-group={group}>{group}</div>}
          {list(items)}
        </div>
      ))}
    </div>
  );
}

export default CrmBlockRuns;
